import {
  isValidElement,
  useEffect,
  useRef,
  type FocusEvent,
  type Key,
  type ReactNode,
  type RefObject,
} from 'react'
import SearchInput, { type SearchInputProps } from './SearchInput'
import { ComboboxListbox, useComboboxListbox } from './combobox'

// Typed `unknown` on purpose: String() is well defined for every value.
const stringify = (value: unknown): string => String(value)

const readField = (value: unknown, field: 'id' | 'value' | 'placeId' | 'name'): unknown => {
  if (typeof value !== 'object' || value === null) return undefined
  if (field === 'id') return 'id' in value ? value.id : undefined
  if (field === 'value') return 'value' in value ? value.value : undefined
  if (field === 'placeId') return 'placeId' in value ? value.placeId : undefined
  return 'name' in value ? value.name : undefined
}

// React coerces every key to a string, so stringifying a non-primitive key keeps the same identity.
const toKey = (value: unknown): Key | undefined => {
  if (value === null || typeof value === 'undefined') return undefined
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'bigint') {
    return value
  }
  return stringify(value)
}

const toLabel = (value: unknown): ReactNode => {
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'bigint' ||
    typeof value === 'boolean' ||
    isValidElement(value)
  ) {
    return value
  }
  return undefined
}

const defaultGetOptionKey = (option: unknown, index: number): Key =>
  toKey(readField(option, 'id')) ?? toKey(readField(readField(option, 'value'), 'placeId')) ?? index
const defaultGetOptionLabel = (option: unknown): ReactNode =>
  toLabel(readField(option, 'name')) ?? stringify(option ?? '')

type AutocompleteSearchInputOwnProps<TOption> = {
  autoComplete?: string
  className?: string
  disabled?: boolean
  getOptionKey?: (option: TOption, index: number) => Key
  getOptionLabel?: (option: TOption) => ReactNode
  inputRef?: RefObject<HTMLInputElement | null>
  minSearchLength?: number
  onClear?: () => void
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void
  onSelect?: (option: TOption) => void
  placeholder?: string
  setValue: (value: string) => void
  suggestions?: readonly TOption[]
  value?: string
}

export type AutocompleteSearchInputProps<TOption> = AutocompleteSearchInputOwnProps<TOption> &
  Omit<SearchInputProps, keyof AutocompleteSearchInputOwnProps<TOption>>

export default function AutocompleteSearchInput<TOption>({
  autoComplete = 'one-time-code',
  className,
  disabled = false,
  getOptionKey = defaultGetOptionKey,
  getOptionLabel = defaultGetOptionLabel,
  inputRef,
  minSearchLength = 3,
  onClear,
  onFocus,
  onSelect,
  placeholder,
  setValue,
  suggestions = [],
  value,
  ...inputProps
}: AutocompleteSearchInputProps<TOption>) {
  const rootRef = useRef<HTMLDivElement>(null)
  const fallbackInputRef = useRef<HTMLInputElement>(null)
  const resolvedInputRef = inputRef ?? fallbackInputRef
  const {
    activateOption,
    activeIndex,
    activeOptionId,
    close,
    handleKeyDown,
    isOpen,
    listboxId,
    openWithFirstOption,
  } = useComboboxListbox({ optionCount: suggestions.length })
  const isSearchReady = String(value ?? '').trim().length >= minSearchLength
  const hasSuggestions = suggestions.length > 0
  const suggestionsIdentity = suggestions
    .map((suggestion, index) => String(getOptionKey(suggestion, index)))
    .join('\u001f')

  useEffect(() => {
    if (!isSearchReady || document.activeElement !== resolvedInputRef.current) {
      close()
      return
    }

    if (hasSuggestions) {
      openWithFirstOption()
    } else {
      close()
    }
  }, [
    close,
    hasSuggestions,
    isSearchReady,
    openWithFirstOption,
    resolvedInputRef,
    suggestionsIdentity,
    suggestions.length,
    value,
  ])

  useEffect(() => {
    if (!isOpen) return undefined

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target
      if (!(target instanceof Node && rootRef.current?.contains(target))) {
        close()
      }
    }

    document.addEventListener('pointerdown', handlePointerDown)

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
    }
  }, [close, isOpen])

  const openIfReady = () => {
    if (isSearchReady && hasSuggestions) {
      openWithFirstOption()
    }
  }

  const clearSearch = () => {
    if (onClear) {
      onClear()
    } else {
      setValue?.('')
    }
    close()
  }

  const selectSuggestion = (suggestion: TOption | undefined) => {
    if (!suggestion) {
      return
    }

    close()
    onSelect?.(suggestion)
  }

  return (
    <div ref={rootRef} className="relative">
      <SearchInput
        {...inputProps}
        inputRef={resolvedInputRef}
        value={value}
        setValue={setValue}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete={autoComplete}
        className={className}
        onClear={clearSearch}
        onFocus={(event) => {
          openIfReady()
          onFocus?.(event)
        }}
        onBlur={close}
        onClick={openIfReady}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={isOpen && hasSuggestions}
        aria-controls={isOpen && hasSuggestions ? listboxId : undefined}
        aria-activedescendant={activeOptionId}
        onKeyDown={(event) => {
          handleKeyDown(event, (index) => selectSuggestion(suggestions[index]))
        }}
      />
      {isOpen && hasSuggestions && (
        <div className="absolute z-30 mt-1 w-full rounded-md bg-popover shadow-md ring-1 ring-foreground/10">
          <ComboboxListbox
            items={suggestions}
            listboxId={listboxId}
            activeIndex={activeIndex}
            getOptionKey={getOptionKey}
            getOptionLabel={getOptionLabel}
            onActivateOption={activateOption}
            onSelectOption={selectSuggestion}
          />
        </div>
      )}
    </div>
  )
}
